import type { SupabaseClient } from "@supabase/supabase-js";

type AnySupabase = SupabaseClient<any, any, any>;

export type TrialExtraction = Record<string, any>;

export interface PersistTrialExtractionResult {
  trialFieldsUpdated: number;
  protocolSaved: boolean;
  visitsInserted: number;
  feeRowsInserted: number;
  armsInserted: number;
  endpointsInserted: number;
  randomizationSaved: boolean;
  ecoaInstrumentsInserted: number;
  formSetRowsInserted: number;
  equipmentCaptured: number;
  warnings: string[];
  summaryBits: string[];
}

const VALID_FEE_CATEGORIES = new Set([
  "visit",
  "procedure",
  "milestone",
  "per_diem",
  "lab",
  "imaging",
  "patient_stipend",
  "other",
]);

function normalizePhase(p: unknown): string | null {
  if (!p) return null;
  const m = String(p).match(/([1-4])/);
  return m ? `Phase ${m[1]}` : null;
}

function validIsoDate(v: unknown): v is string {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function asArray<T = any>(value: unknown): T[] {
  if (Array.isArray(value)) return value.filter(Boolean) as T[];
  // Unwrap common Gemini shapes: { items: [...] }, { arms: [...] }, { visits: [...] }, { doses: [...] }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const k of ["items", "arms", "visits", "doses", "endpoints", "list", "data"]) {
      if (Array.isArray(obj[k])) return (obj[k] as unknown[]).filter(Boolean) as T[];
    }
  }
  return [];
}

function uniqueStrings(...lists: unknown[][]): string[] {
  return Array.from(new Set(lists.flat().filter((v): v is string => typeof v === "string" && v.trim().length > 0)));
}

function visitKey(item: unknown): string {
  if (!item || typeof item !== "object") return String(item ?? "").trim().toLowerCase();
  const v = item as Record<string, unknown>;
  return [
    v.visit_code ?? v.code ?? "",
    v.visit_name ?? v.name ?? v.label ?? v.visit ?? "",
    v.day ?? v.day_number ?? "",
  ]
    .map((part) => String(part).trim().toLowerCase())
    .join("|");
}

function uniqueByKey<T>(items: T[], keyer: (item: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyer(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function getVisitName(v: Record<string, unknown>, index: number): string {
  return String(v.visit_name ?? v.name ?? v.label ?? v.visit ?? v.visit_code ?? v.code ?? `Visit ${index + 1}`).trim();
}

function getVisitCode(v: Record<string, unknown>, index: number): string | null {
  const raw = v.visit_code ?? v.code ?? v.visit_id ?? null;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  const name = getVisitName(v, index);
  return name ? name.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || `V${index + 1}` : `V${index + 1}`;
}

function getVisitDay(v: Record<string, unknown>, fallback: number | null = null): number | null {
  const raw = v.day ?? v.day_number ?? v.study_day ?? v.target_day;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() && Number.isFinite(Number(raw))) return Number(raw);
  return fallback;
}

function getVisitProcedures(v: Record<string, unknown>): string[] {
  const raw = v.procedures ?? v.assessments ?? v.activities ?? v.required_procedures;
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => String(p).trim()).filter(Boolean);
}

function normalizeFeeCategory(raw: unknown): string {
  const s = String(raw || "").toLowerCase().trim();
  if (["per_visit", "screening", "randomization", "completion", "screen_failure", "unscheduled"].includes(s)) return "visit";
  if (["startup", "start_up", "overhead"].includes(s)) return "milestone";
  if (VALID_FEE_CATEGORIES.has(s)) return s;
  return "other";
}

function normalizeCurrency(raw: unknown): string {
  const s = String(raw || "USD").trim().toUpperCase();
  return s.slice(0, 3) || "USD";
}

function feeKey(name: string, category: string, visitCode: string | null): string {
  return `${name.toLowerCase()}|${category.toLowerCase()}|${(visitCode || "").toLowerCase()}`;
}

export function buildExtractionSummary(ex: TrialExtraction, result: Omit<PersistTrialExtractionResult, "summaryBits">): string[] {
  const inclusion = asArray(ex.inclusion_criteria);
  const exclusion = asArray(ex.exclusion_criteria);
  const visits = asArray(ex.visit_schedule);
  const primaryEndpoints = asArray(ex.primary_endpoints);
  const secondaryEndpoints = asArray(ex.secondary_endpoints);
  const exploratoryEndpoints = asArray(ex.exploratory_endpoints);
  const dosing = asArray(ex.dosing_schedule);
  const safety = asArray(ex.safety_considerations);
  const arms = asArray(ex.arms);

  const bits: string[] = [];
  if (ex.phase) bits.push(`Phase ${String(ex.phase).replace(/\D/g, "") || ex.phase}`);
  if (ex.protocol_version) bits.push(`v${String(ex.protocol_version).replace(/^v/i, "")}`);
  if (typeof ex.target_enrollment === "number") bits.push(`n=${ex.target_enrollment}`);
  if (arms.length) bits.push(`${arms.length} arms`);
  if (inclusion.length) bits.push(`${inclusion.length} incl`);
  if (exclusion.length) bits.push(`${exclusion.length} excl`);
  if (visits.length) bits.push(`${visits.length} visits`);
  if (primaryEndpoints.length) bits.push(`${primaryEndpoints.length} PE`);
  if (secondaryEndpoints.length || exploratoryEndpoints.length) bits.push(`${secondaryEndpoints.length + exploratoryEndpoints.length} SE/EE`);
  if (dosing.length) bits.push(`${dosing.length} doses`);
  if (safety.length) bits.push(`${safety.length} safety`);
  if (result.visitsInserted) bits.push(`+${result.visitsInserted} SoA rows`);
  if (result.feeRowsInserted) bits.push(`+${result.feeRowsInserted} fees/stipends`);
  if (result.armsInserted) bits.push(`+${result.armsInserted} arms`);
  if (result.endpointsInserted) bits.push(`+${result.endpointsInserted} endpoints`);
  if (result.randomizationSaved) bits.push(`rand scheme`);
  if (result.ecoaInstrumentsInserted) bits.push(`+${result.ecoaInstrumentsInserted} eCOA`);
  if (result.formSetRowsInserted) bits.push(`+${result.formSetRowsInserted} form/visit`);
  if (result.equipmentCaptured) bits.push(`+${result.equipmentCaptured} tools`);
  if (result.warnings?.length) bits.push(result.warnings[0]);
  return bits;
}

export async function persistTrialExtraction(
  supabase: AnySupabase,
  trialId: string,
  ex: TrialExtraction,
): Promise<PersistTrialExtractionResult> {
  const trialPatch: Record<string, unknown> = {};
  const phase = normalizePhase(ex.phase);
  if (phase) trialPatch.phase = phase;
  if (typeof ex.target_enrollment === "number") trialPatch.target_enrollment = ex.target_enrollment;
  if (ex.protocol_version) trialPatch.protocol_version = String(ex.protocol_version);
  if (ex.nct_id) trialPatch.nct_id = String(ex.nct_id);
  if (ex.title && typeof ex.title === "string" && ex.title.length > 20) trialPatch.title = ex.title;
  if (validIsoDate(ex.start_date)) trialPatch.start_date = ex.start_date;
  if (validIsoDate(ex.expected_completion_date)) trialPatch.expected_completion_date = ex.expected_completion_date;

  if (Object.keys(trialPatch).length > 0) {
    const { error } = await supabase.from("cro_trials").update(trialPatch).eq("id", trialId);
    if (error) throw new Error(`cro_trials update failed: ${error.message}`);
  }

  const inclusion = asArray<string>(ex.inclusion_criteria);
  const exclusion = asArray<string>(ex.exclusion_criteria);
  const visits = asArray<any>(ex.visit_schedule);
  const primaryEndpoints = asArray<string>(ex.primary_endpoints);
  const secondaryEndpoints = asArray<string>(ex.secondary_endpoints);
  const exploratoryEndpoints = asArray<string>(ex.exploratory_endpoints);
  const dosing = asArray<any>(ex.dosing_schedule);
  const safety = asArray<string>(ex.safety_considerations);
  const arms = asArray<any>(ex.arms);
  const warnings: string[] = [];
  const protoNumber =
    (ex.protocol_number && String(ex.protocol_number)) ||
    (ex.nct_id && String(ex.nct_id)) ||
    `TRIAL-${trialId.slice(0, 8)}`;

  const hasProtocolData =
    inclusion.length || exclusion.length || visits.length || primaryEndpoints.length || secondaryEndpoints.length ||
    exploratoryEndpoints.length || dosing.length || safety.length || arms.length || ex.protocol_version || ex.study_design ||
    ex.population || ex.indication || ex.intervention_name || ex.intervention_code;

  let protocolSaved = false;
  if (hasProtocolData) {
    const { data: existing, error: existingErr } = await supabase
      .from("trial_protocols")
      .select("id, protocol_version, inclusion_criteria, exclusion_criteria, visit_schedule, primary_endpoints, secondary_endpoints, dosing_schedule, safety_considerations, eligibility_requirements")
      .eq("trial_id", trialId)
      .maybeSingle();
    if (existingErr) throw new Error(`trial_protocols load failed: ${existingErr.message}`);

    const existingInc = ((existing?.inclusion_criteria as any)?.items ?? []) as string[];
    const existingExc = ((existing?.exclusion_criteria as any)?.items ?? []) as string[];
    const existingVisits = asArray(existing?.visit_schedule);
    const existingPE = ((existing?.primary_endpoints as any)?.items ?? []) as string[];
    const existingSE = ((existing?.secondary_endpoints as any)?.items ?? []) as string[];
    const existingSafety = ((existing?.safety_considerations as any)?.items ?? []) as string[];
    const existingDosing = ((existing?.dosing_schedule as any)?.doses ?? []) as unknown[];

    const eligibilityBlob: Record<string, unknown> = { ...((existing?.eligibility_requirements as any) ?? {}) };
    if (ex.population) eligibilityBlob.population = ex.population;
    if (ex.eligibility_notes) eligibilityBlob.notes = ex.eligibility_notes;
    if (ex.indication) eligibilityBlob.indication = ex.indication;
    if (ex.intervention_name) eligibilityBlob.intervention_name = ex.intervention_name;
    if (ex.intervention_code) eligibilityBlob.intervention_code = ex.intervention_code;
    if (ex.intervention_route) eligibilityBlob.intervention_route = ex.intervention_route;
    if (ex.study_design) eligibilityBlob.study_design = ex.study_design;
    if (ex.randomization_ratio) eligibilityBlob.randomization_ratio = ex.randomization_ratio;
    if (protoNumber) eligibilityBlob.protocol_number = protoNumber;
    if (arms.length) eligibilityBlob.arms = arms;
    if (ex.sponsor_name) eligibilityBlob.sponsor_name = ex.sponsor_name;
    if (ex.sponsor_contact_name) eligibilityBlob.sponsor_contact_name = ex.sponsor_contact_name;
    if (ex.sponsor_contact_email) eligibilityBlob.sponsor_contact_email = ex.sponsor_contact_email;

    const payload: Record<string, unknown> = {
      inclusion_criteria: { items: uniqueStrings(existingInc, inclusion) },
      exclusion_criteria: { items: uniqueStrings(existingExc, exclusion) },
      primary_endpoints: { items: uniqueStrings(existingPE, primaryEndpoints) },
      secondary_endpoints: { items: uniqueStrings(existingSE, secondaryEndpoints, exploratoryEndpoints) },
      safety_considerations: { items: uniqueStrings(existingSafety, safety) },
      visit_schedule: { visits: uniqueByKey([...existingVisits, ...visits], visitKey) },
      dosing_schedule: { doses: [...existingDosing, ...dosing] },
      eligibility_requirements: Object.keys(eligibilityBlob).length ? eligibilityBlob : {},
      protocol_version: ex.protocol_version || existing?.protocol_version || "v1.0",
      last_updated: new Date().toISOString(),
    };

    if (existing?.id) {
      const { error } = await supabase.from("trial_protocols").update(payload).eq("id", existing.id);
      if (error) throw new Error(`trial_protocols update failed: ${error.message}`);
    } else {
      const { error } = await supabase.from("trial_protocols").insert({ trial_id: trialId, ...payload } as any);
      if (error) throw new Error(`trial_protocols insert failed: ${error.message}`);
    }
    protocolSaved = true;
  }

  // study_* protocol_id columns FK to study_protocols (site-scoped table with
  // RLS gated by user_is_site_staff). Resolve/create that row once and reuse it
  // for visits, arms, and endpoints; do not use trial_protocols.id here.
  let studyProtocolId: string | null = null;
  if (hasProtocolData) {
    const { data: trialRow } = await supabase
      .from("cro_trials")
      .select("id, title, phase, target_enrollment, cro_organization_id")
      .eq("id", trialId)
      .maybeSingle();

    const { data: linkRows, error: linkErr } = await supabase
      .from("cro_trial_sites")
      .select("id, trial_site_id, site_organization_id")
      .eq("cro_trial_id", trialId)
      .limit(10);
    if (linkErr) warnings.push(`Trial-site link lookup failed: ${linkErr.message}`);

    let trialSiteId = (linkRows as any[] | null)?.find((r) => r.trial_site_id)?.trial_site_id ?? null;
    const linkedSiteOrganizationId = (linkRows as any[] | null)?.find((r) => r.site_organization_id)?.site_organization_id ?? null;
    let organizationId = linkedSiteOrganizationId ?? (trialRow as any)?.cro_organization_id ?? null;

    if (!trialSiteId && linkedSiteOrganizationId) {
      const { data: siteRows, error: siteErr } = await supabase
        .from("trial_sites")
        .select("id, organization_id")
        .eq("organization_id", linkedSiteOrganizationId)
        .eq("status", "active")
        .limit(2);
      if (siteErr) {
        warnings.push(`Active trial site lookup failed: ${siteErr.message}`);
      } else if ((siteRows ?? []).length === 1) {
        trialSiteId = (siteRows as any[])[0].id;
        organizationId = (siteRows as any[])[0].organization_id ?? organizationId;
        const linkToBackfill = (linkRows as any[] | null)?.find((r) => !r.trial_site_id)?.id;
        if (linkToBackfill) {
          const { error: backfillErr } = await supabase
            .from("cro_trial_sites")
            .update({ trial_site_id: trialSiteId })
            .eq("id", linkToBackfill);
          if (backfillErr) warnings.push(`Site link found, but could not be backfilled: ${backfillErr.message}`);
        }
      } else if ((siteRows ?? []).length > 1) {
        warnings.push("Protocol JSON saved; site-scoped visit calendar is pending because multiple active site records exist.");
      }
    }

    if (trialSiteId && trialRow) {
      const { data: existingProto } = await supabase
        .from("study_protocols")
        .select("id, inclusion_criteria, exclusion_criteria")
        .eq("trial_site_id", trialSiteId)
        .eq("protocol_number", protoNumber)
        .maybeSingle();

      studyProtocolId = (existingProto?.id as string | undefined) ?? null;
      if (studyProtocolId) {
        const { error: updateProtoErr } = await supabase
          .from("study_protocols")
          .update({
            protocol_title: (trialRow as any).title || protoNumber,
            phase: (trialRow as any).phase || phase || undefined,
            protocol_version: ex.protocol_version ? String(ex.protocol_version) : undefined,
            indication: ex.indication ? String(ex.indication) : undefined,
            target_enrollment: (trialRow as any).target_enrollment || undefined,
            number_of_arms: arms.length || undefined,
            inclusion_criteria: uniqueStrings(((existingProto as any)?.inclusion_criteria ?? []) as string[], inclusion),
            exclusion_criteria: uniqueStrings(((existingProto as any)?.exclusion_criteria ?? []) as string[], exclusion),
          } as any)
          .eq("id", studyProtocolId);
        if (updateProtoErr) warnings.push(`study_protocols update warning: ${updateProtoErr.message}`);
      } else {
        const { data: created, error: createErr } = await supabase
          .from("study_protocols")
          .insert({
            trial_site_id: trialSiteId,
            organization_id: organizationId,
            protocol_number: protoNumber,
            protocol_title: (trialRow as any).title || protoNumber,
            phase: (trialRow as any).phase || phase || "Phase I",
            protocol_version: ex.protocol_version ? String(ex.protocol_version) : null,
            indication: ex.indication ? String(ex.indication) : null,
            target_enrollment: (trialRow as any).target_enrollment || null,
            therapeutic_area: ex.therapeutic_area ? String(ex.therapeutic_area) : ex.indication ? String(ex.indication) : null,
            primary_objective: ex.primary_objective ? String(ex.primary_objective) : null,
            number_of_arms: arms.length || null,
            inclusion_criteria: inclusion,
            exclusion_criteria: exclusion,
            status: "active",
          } as any)
          .select("id")
          .single();
        if (createErr) {
          warnings.push(`study_protocols create failed: ${createErr.message}`);
        } else {
          studyProtocolId = (created?.id as string | undefined) ?? null;
        }
      }
    } else if (visits.length || arms.length || primaryEndpoints.length || secondaryEndpoints.length || exploratoryEndpoints.length) {
      warnings.push("Protocol JSON saved, but site-scoped visit calendar is pending site assignment.");
    }
  }

  let visitsInserted = 0;
  if (visits.length && studyProtocolId) {
    const { data: existingVisits } = await supabase
      .from("study_visits")
      .select("visit_code, visit_name, day_number")
      .eq("protocol_id", studyProtocolId);
    const key = (c: string | null, n: string, day: number | null) => `${(c || "").toLowerCase()}|${n.toLowerCase()}|${day ?? ""}`;
    const have = new Set((existingVisits ?? []).map((v: any) => key(v.visit_code, v.visit_name, v.day_number ?? null)));
    const rows = visits
      .map((raw: any, i: number) => {
        const v = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
        const visitName = getVisitName(v, i);
        const visitCode = getVisitCode(v, i);
        const dayNumber = getVisitDay(v, null);
        return {
          raw: v,
          row: {
        protocol_id: studyProtocolId,
            visit_name: visitName,
            visit_code: visitCode,
            visit_type: v.type ?? v.visit_type ?? "scheduled",
            day_number: dayNumber,
            window_before: typeof v.window_before === "number" ? v.window_before : 0,
            window_after: typeof v.window_after === "number" ? v.window_after : 0,
            procedures: getVisitProcedures(v),
        is_required: true,
        display_order: i,
          },
          key: key(visitCode, visitName, dayNumber),
        };
      })
      .filter(({ row, key: rowKey }: any) => row.visit_name && !have.has(rowKey))
      .map(({ row }: any) => row);
    if (rows.length) {
      const { error } = await supabase.from("study_visits").insert(rows as any);
      if (error) {
        warnings.push(`study_visits insert failed: ${error.message}`);
      } else {
        visitsInserted = rows.length;
      }
    }
  } else if (visits.length && !studyProtocolId) {
    warnings.push("Visit schedule saved in protocol JSON, but no site-scoped study protocol exists yet.");
  }


  const sitePayments = asArray<any>(ex.site_payment_schedule);
  const stipends = asArray<any>(ex.patient_stipend_schedule);
  let feeRowsInserted = 0;
  if (sitePayments.length || stipends.length) {
    const { data: existingFees, error: existingFeesErr } = await supabase
      .from("protocol_fee_schedules")
      .select("fee_name, fee_category, visit_code")
      .eq("trial_id", trialId);
    if (existingFeesErr) throw new Error(`protocol_fee_schedules load failed: ${existingFeesErr.message}`);

    const haveFees = new Set((existingFees ?? []).map((f: any) => feeKey(f.fee_name, f.fee_category, f.visit_code)));
    const today = new Date().toISOString().slice(0, 10);

    const siteRows = sitePayments
      .filter((p: any) => p?.fee_name && typeof p.amount === "number")
      .map((p: any) => ({
        trial_id: trialId,
        fee_name: String(p.fee_name),
        fee_description: p.description ?? null,
        fee_category: normalizeFeeCategory(p.fee_category),
        amount: Number(p.amount),
        currency: normalizeCurrency(p.currency),
        is_active: true,
        effective_from: today,
        event_trigger_type: p.event_trigger_type || (p.visit_code ? "per_visit" : "manual_only"),
        visit_code: p.visit_code ?? null,
      }))
      .filter((p: any) => !haveFees.has(feeKey(p.fee_name, p.fee_category, p.visit_code)));

    const stipendRows = stipends
      .filter((s: any) => s?.stipend_name && typeof s.amount === "number")
      .map((s: any) => ({
        trial_id: trialId,
        fee_name: String(s.stipend_name),
        fee_description: s.description ?? `Participant stipend (${s.stipend_type || "visit_stipend"})`,
        fee_category: "patient_stipend",
        amount: Number(s.amount),
        currency: normalizeCurrency(s.currency),
        is_active: true,
        effective_from: today,
        event_trigger_type: s.visit_code ? "per_visit" : "per_patient",
        visit_code: s.visit_code ?? null,
      }))
      .filter((s: any) => !haveFees.has(feeKey(s.fee_name, s.fee_category, s.visit_code)));

    const allFeeRows = [...siteRows, ...stipendRows];
    if (allFeeRows.length) {
      const { error } = await supabase.from("protocol_fee_schedules").insert(allFeeRows as any);
      if (error) throw new Error(`protocol_fee_schedules insert failed: ${error.message}`);
      feeRowsInserted = allFeeRows.length;
    }
  }

  // Resolve protocol id (created earlier if hasProtocolData) for downstream inserts.
  let protocolId: string | null = null;
  {
    const { data: p } = await supabase.from("trial_protocols").select("id").eq("trial_id", trialId).maybeSingle();
    protocolId = p?.id ?? null;
  }

  // ---- Study arms ----
  let armsInserted = 0;
  if (studyProtocolId && arms.length) {
    const { data: existingArms } = await supabase.from("study_arms").select("arm_name").eq("protocol_id", studyProtocolId);
    const have = new Set((existingArms ?? []).map((a: any) => String(a.arm_name).toLowerCase()));
    const armRows = arms
      .filter((a: any) => a?.name && !have.has(String(a.name).toLowerCase()))
      .map((a: any, i: number) => ({
        protocol_id: studyProtocolId,
        arm_name: String(a.name),
        arm_type: a.arm_type ?? null,
        description: a.description ?? null,
        target_enrollment: typeof a.n === "number" ? a.n : null,
        display_order: i,
      }));
    if (armRows.length) {
      const { error } = await supabase.from("study_arms").insert(armRows as any);
      if (!error) armsInserted = armRows.length;
      else warnings.push(`study_arms insert failed: ${error.message}`);
    }
  } else if (arms.length && !studyProtocolId) {
    warnings.push("Study arms saved in protocol JSON, but no site-scoped study protocol exists yet.");
  }

  // ---- Study endpoints ----
  let endpointsInserted = 0;
  if (studyProtocolId) {
    const endpointRows: any[] = [];
    const pushEP = (list: string[], type: string) => {
      list.forEach((name) => {
        if (name && typeof name === "string") {
          endpointRows.push({ protocol_id: studyProtocolId, endpoint_type: type, endpoint_name: name.slice(0, 500), description: name });
        }
      });
    };
    pushEP(primaryEndpoints, "primary");
    pushEP(secondaryEndpoints, "secondary");
    pushEP(exploratoryEndpoints, "exploratory");

    if (endpointRows.length) {
      const { data: existingEP } = await supabase.from("study_endpoints").select("endpoint_name").eq("protocol_id", studyProtocolId);
      const have = new Set((existingEP ?? []).map((e: any) => String(e.endpoint_name).toLowerCase()));
      const fresh = endpointRows.filter((r) => !have.has(String(r.endpoint_name).toLowerCase()));
      if (fresh.length) {
        const { error } = await supabase.from("study_endpoints").insert(fresh as any);
        if (!error) endpointsInserted = fresh.length;
        else warnings.push(`study_endpoints insert failed: ${error.message}`);
      }
    }
  } else if ((primaryEndpoints.length || secondaryEndpoints.length || exploratoryEndpoints.length) && !studyProtocolId) {
    warnings.push("Study endpoints saved in protocol JSON, but no site-scoped study protocol exists yet.");
  }

  // ---- Randomization scheme ----
  let randomizationSaved = false;
  const rand = ex.randomization as any;
  if (rand && rand.type && rand.type !== "none") {
    const { data: existingRand } = await supabase
      .from("randomization_schemes")
      .select("id")
      .eq("trial_id", trialId)
      .maybeSingle();
    if (!existingRand?.id) {
      const stratFactors = Array.isArray(rand.stratification_factors) ? rand.stratification_factors : [];
      const { error } = await supabase.from("randomization_schemes").insert({
        trial_id: trialId,
        scheme_name: `${String(rand.type)} ${rand.allocation_ratio || ex.randomization_ratio || ""}`.trim(),
        randomization_type: String(rand.type),
        block_size: typeof rand.block_size === "number" ? rand.block_size : null,
        allocation_ratio: rand.allocation_ratio || ex.randomization_ratio || null,
        is_blinded: !!rand.is_blinded,
        stratification_method: rand.blinding ?? null,
        stratification_factors: stratFactors.length ? { factors: stratFactors } : null,
        is_active: true,
      } as any);
      if (!error) randomizationSaved = true;
    }
  }

  // ---- eCOA instruments (trial-scoped questionnaires) + form-set mappings ----
  const ecoaInstruments = asArray<any>(ex.ecoa_instruments);
  const ecrfForms = asArray<any>(ex.ecrf_forms);
  let ecoaInstrumentsInserted = 0;
  let formSetRowsInserted = 0;
  const ecoaIdByCode = new Map<string, string>();
  if (ecoaInstruments.length) {
    const { data: existingQ } = await supabase
      .from("ecoa_questionnaires")
      .select("id, questionnaire_name")
      .eq("trial_id", trialId);
    const haveQ = new Map<string, string>((existingQ ?? []).map((q: any) => [String(q.questionnaire_name).toLowerCase(), q.id]));
    for (const inst of ecoaInstruments) {
      const name = String(inst.instrument_name || inst.instrument_code || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      let id = haveQ.get(key);
      if (!id) {
        const { data: ins, error } = await supabase
          .from("ecoa_questionnaires")
          .insert({
            trial_id: trialId,
            questionnaire_name: name,
            questionnaire_version: "1.0",
            description: inst.instrument_code || null,
            assessment_type: inst.assessment_type || "PRO",
            form_schema: { source: "ai_extraction", items: [] },
            estimated_duration_minutes: typeof inst.estimated_minutes === "number" ? inst.estimated_minutes : null,
            is_active: true,
            status: "draft",
          } as any)
          .select("id")
          .maybeSingle();
        if (!error && ins?.id) {
          id = ins.id;
          ecoaInstrumentsInserted += 1;
        }
      }
      if (id) {
        ecoaIdByCode.set(key, id);
        const visits: string[] = Array.isArray(inst.visit_codes) ? inst.visit_codes : [];
        if (visits.length) {
          const rows = visits.map((vc: string, i: number) => ({
            cro_trial_id: trialId,
            form_type: "ecoa",
            ecoa_questionnaire_id: id,
            required: true,
            visit_type: vc,
            display_order: i,
            is_active: true,
          }));
          const { error: fsErr, count } = await supabase
            .from("trial_protocol_form_sets")
            .insert(rows as any, { count: "exact" });
          if (!fsErr && typeof count === "number") formSetRowsInserted += count;
          else if (!fsErr) formSetRowsInserted += rows.length;
        }
      }
    }
  }

  // ---- eCRF form-set mapping (store as generic form_set rows referencing form_type) ----
  if (ecrfForms.length) {
    const rows: any[] = [];
    ecrfForms.forEach((f: any, i: number) => {
      const visits: string[] = Array.isArray(f.visit_codes) && f.visit_codes.length ? f.visit_codes : [null];
      visits.forEach((vc, j) => {
        rows.push({
          cro_trial_id: trialId,
          form_type: f.form_type || "ecrf",
          required: f.required !== false,
          visit_type: vc,
          display_order: i * 100 + j,
          notes: f.form_name ? String(f.form_name) : null,
          is_active: true,
        });
      });
    });
    if (rows.length) {
      const { error } = await supabase.from("trial_protocol_form_sets").insert(rows as any);
      if (!error) formSetRowsInserted += rows.length;
    }
  }

  // ---- Equipment & tools (persisted in trial_protocols.eligibility_requirements.equipment) ----
  const equipment = asArray<any>(ex.equipment_and_tools);
  const sampleCollections = asArray<any>(ex.sample_collections);
  const biomarkers = asArray<string>(ex.biomarkers);
  let equipmentCaptured = 0;
  if (protocolId && (equipment.length || sampleCollections.length || biomarkers.length)) {
    const { data: current } = await supabase.from("trial_protocols").select("eligibility_requirements").eq("id", protocolId).maybeSingle();
    const blob: Record<string, unknown> = { ...((current?.eligibility_requirements as any) ?? {}) };
    if (equipment.length) blob.equipment = equipment;
    if (sampleCollections.length) blob.sample_collections = sampleCollections;
    if (biomarkers.length) blob.biomarkers = biomarkers;
    const { error } = await supabase.from("trial_protocols").update({ eligibility_requirements: blob }).eq("id", protocolId);
    if (!error) equipmentCaptured = equipment.length + sampleCollections.length + biomarkers.length;
  }

  const result = {
    trialFieldsUpdated: Object.keys(trialPatch).length,
    protocolSaved,
    visitsInserted,
    feeRowsInserted,
    armsInserted,
    endpointsInserted,
    randomizationSaved,
    ecoaInstrumentsInserted,
    formSetRowsInserted,
    equipmentCaptured,
    warnings,
  };

  return { ...result, summaryBits: buildExtractionSummary(ex, result) };
}
